import type { FieldType } from "./types";

/** True when any filter field has a non-empty value (search, selects, date ranges, etc.). */
export function filterFieldsHaveActiveValues(fields: FieldType[]): boolean {
  return fields.some((field) => {
    const v = field.fieldValue as unknown;
    if (v == null) return false;
    // Select/switch often use `false` (e.g. Inactive); Boolean(false) would wrongly skip it.
    if (typeof v === "boolean") return true;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "string") return v.trim().length > 0;
    return Boolean(v);
  });
}

/** Serializes a table cell value for CSV export (arrays, nested objects, primitives). */
export const formatCellForCsvExport = (value: unknown): string => {
  if (value == null) return "";

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "object") {
          const record = item as Record<string, unknown>;
          const label = record.name ?? record.fullName ?? record.label;
          return label != null ? String(label).trim() : "";
        }
        return String(item);
      })
      .filter((part) => part.length > 0)
      .join(", ");
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const name = record.fullName ?? record.name;
    const membershipId = record.membershipId;
    return [name, membershipId]
      .filter((part) => part != null && String(part).trim().length > 0)
      .map(String)
      .join(" ")
      .trim();
  }

  return String(value);
};

export const downloadCSV = <T extends object>(
  data: T[],
  filename = "data.csv",
  mutateDataBeforeDownload?: (data: T[]) => any[],
) => {
  if (data.length === 0) {
    console.warn("No data available to download.");
    return;
  }

  if (mutateDataBeforeDownload) {
    data = mutateDataBeforeDownload(data);
  }
  data = data.map((currentItem: any) => {
    if (currentItem.translations) {
      return {
        ...currentItem,
        translations: JSON.stringify(currentItem.translations),
      };
    }
    return currentItem;
  });

  // Extract headers dynamically from the object keys
  const csvHeaders = Object.keys(data[0]);

  // Convert data to CSV rows
  const csvRows = data.map((row) =>
    csvHeaders
      .map((header) => {
        const cellValue = formatCellForCsvExport((row as any)[header]);
        return `"${cellValue.replace(/"/g, '""')}"`;
      })
      .join(","),
  );

  // Prepare CSV content
  const csvContent = [csvHeaders.join(","), ...csvRows].join("\n");

  // Create a downloadable blob
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  // Create a temporary download link and trigger the download
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
export const getLabelFromKey = (key: string | undefined | null): string => {
  if (key == null) {
    return "--";
  }
  return key
    .split("_") // Split words by underscore
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
    .join(" "); // Join them with spaces
};

export const exportCsv = <T extends Record<string, any>>(
  data: T[],
  filename: string = "export.csv",
) => {
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  // Get all unique keys from all objects to handle varying object structures
  const allKeys = Array.from(new Set(data.flatMap((obj) => Object.keys(obj))));

  // Create CSV header
  const csvHeader = allKeys.map((key) => `"${String(key)}"`).join(",");

  // Create CSV rows
  const csvRows = data.map((row) => {
    return allKeys
      .map((key) => {
        const stringValue = formatCellForCsvExport(row[key]).replace(/"/g, '""');
        return `"${stringValue}"`;
      })
      .join(",");
  });

  // Combine header and rows
  const csvContent = [csvHeader, ...csvRows].join("\n");

  // Create and trigger download
  downloadCSVContent(csvContent, filename);
};

export const downloadCSVContent = (csvContent: string, filename: string) => {
  // Add BOM for UTF-8 encoding (helps with special characters)
  const BOM = "\uFEFF";
  const csvWithBOM = BOM + csvContent;

  // Create blob and download
  const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      filename.endsWith(".csv") ? filename : `${filename}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};
